import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/admin/PageHeader";
import { MediaUrlField } from "@/components/admin/MediaPicker";
import { adminCollectionQuery, adminCollectionsQuery } from "@/lib/collections/queries";
import { saveCollectionFn, setCollectionStatusFn } from "@/lib/collections/collections.functions";
import {
  COLLECTION_STATUSES,
  emptyCollection,
  slugify,
  type CollectionInput,
  type CollectionStatus,
} from "@/lib/collections/types";

export const Route = createFileRoute("/admin/collections/$id")({ component: CollectionEditor });

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-black/50">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-black/45">{hint}</span>}
    </label>
  );
}

const input = "w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#C6A15B]";

function CollectionEditor() {
  const { id } = Route.useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: existing, isLoading } = useQuery(adminCollectionQuery(id));
  const { data: all = [] } = useQuery(adminCollectionsQuery());
  const [form, setForm] = useState<CollectionInput>(emptyCollection());
  const [slugTouched, setSlugTouched] = useState(!isNew);

  useEffect(() => {
    if (!existing) return;
    setForm({
      id: existing.id,
      slug: existing.slug,
      name: existing.name,
      shortDescription: existing.shortDescription,
      longDescription: existing.longDescription,
      bannerImage: existing.bannerImage,
      thumbnailImage: existing.thumbnailImage,
      seo: existing.seo,
      status: existing.status,
      isFeatured: existing.isFeatured,
      displayOrder: existing.displayOrder,
      parentId: existing.parentId,
      scheduledAt: existing.scheduledAt ? existing.scheduledAt.slice(0, 16) : null,
    });
    setSlugTouched(true);
  }, [existing]);

  const parents = useMemo(() => all.filter((c) => c.id !== existing?.id), [all, existing?.id]);

  function set<K extends keyof CollectionInput>(k: K, v: CollectionInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const saveMut = useMutation({
    mutationFn: (payload: CollectionInput) => saveCollectionFn({ data: payload }),
    onSuccess: (res, payload) => {
      void qc.invalidateQueries({ queryKey: ["admin", "collections"] });
      toast.success(payload.status === "published" ? "Collection published — live on the website" : "Collection saved");
      if (isNew) void navigate({ to: "/admin/collections/$id", params: { id: res.id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const statusMut = useMutation({
    mutationFn: (status: CollectionStatus) => setCollectionStatusFn({ data: { ids: [id], status } }),
    onSuccess: (_r, status) => {
      void qc.invalidateQueries({ queryKey: ["admin", "collections"] });
      set("status", status);
      toast.success(`Collection ${status}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit(status?: CollectionStatus) {
    const payload: CollectionInput = { ...form, status: status ?? form.status };
    if (!payload.name?.trim()) return toast.error("Name is required");
    if (!payload.slug?.trim()) return toast.error("Slug is required");
    if (payload.status === "scheduled" && !payload.scheduledAt) return toast.error("Pick a schedule date");
    setForm(payload);
    saveMut.mutate(payload);
  }

  if (!isNew && isLoading) return <p className="text-sm text-black/50">Loading collection…</p>;
  if (!isNew && !existing) return <p className="text-sm text-black/50">Collection not found.</p>;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={isNew ? "New collection" : form.name || "Edit collection"}
        subtitle={isNew ? "Create a collection — it stays hidden until published." : `/${form.slug} · ${form.status}`}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/collections" className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]">Back</Link>
            <button type="button" disabled={saveMut.isPending} onClick={() => submit()} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]">Save</button>
            <button type="button" disabled={saveMut.isPending} onClick={() => submit("published")} className="border border-black bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:border-[#C6A15B] hover:bg-[#C6A15B]">Publish</button>
            {!isNew && form.status === "published" && (
              <button type="button" onClick={() => statusMut.mutate("draft")} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]">Unpublish</button>
            )}
            {!isNew && (
              <button type="button" onClick={() => statusMut.mutate("archived")} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]">Archive</button>
            )}
          </div>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-5">
          <section className="grid gap-4 border border-black/10 bg-white p-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">Content</h3>
            <Field label="Name">
              <input
                className={input}
                value={form.name}
                onChange={(e) => {
                  set("name", e.target.value);
                  if (!slugTouched) set("slug", slugify(e.target.value));
                }}
              />
            </Field>
            <Field label="Slug" hint="Website address: /collections/<slug>">
              <input className={input} value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} />
            </Field>
            <Field label="Short description" hint="Shown on collection cards and listings.">
              <input className={input} value={form.shortDescription ?? ""} onChange={(e) => set("shortDescription", e.target.value)} />
            </Field>
            <Field label="Long description">
              <textarea rows={6} className={input} value={form.longDescription ?? ""} onChange={(e) => set("longDescription", e.target.value)} />
            </Field>
          </section>

          <section className="grid gap-4 border border-black/10 bg-white p-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">Imagery</h3>
            <MediaUrlField label="Banner image" value={form.bannerImage ?? ""} onChange={(v) => set("bannerImage", v)} />
            {form.bannerImage && <img src={form.bannerImage} alt="" className="h-40 w-full object-cover" />}
            <MediaUrlField label="Thumbnail" value={form.thumbnailImage ?? ""} onChange={(v) => set("thumbnailImage", v)} />
            {form.thumbnailImage && <img src={form.thumbnailImage} alt="" className="h-24 w-24 object-cover" />}
          </section>

          <section className="grid gap-4 border border-black/10 bg-white p-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">SEO</h3>
            <Field label="SEO title">
              <input className={input} value={form.seo?.title ?? ""} onChange={(e) => set("seo", { ...form.seo, title: e.target.value })} />
            </Field>
            <Field label="SEO description">
              <textarea rows={3} className={input} value={form.seo?.description ?? ""} onChange={(e) => set("seo", { ...form.seo, description: e.target.value })} />
            </Field>
            <MediaUrlField label="OG image" value={form.seo?.ogImage ?? ""} onChange={(v) => set("seo", { ...form.seo, ogImage: v })} />
          </section>
        </div>

        <aside className="flex flex-col gap-5">
          <section className="grid gap-4 border border-black/10 bg-white p-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">Publishing</h3>
            <Field label="Status" hint="Only Published collections appear on the website.">
              <select className={input} value={form.status} onChange={(e) => set("status", e.target.value as CollectionStatus)}>
                {COLLECTION_STATUSES.map((s) => (
                  <option key={s} value={s}>{s[0]!.toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </Field>
            {form.status === "scheduled" && (
              <Field label="Go live at">
                <input type="datetime-local" className={input} value={form.scheduledAt ?? ""} onChange={(e) => set("scheduledAt", e.target.value)} />
              </Field>
            )}
            <label className="flex items-center gap-2 text-sm text-black/70">
              <input type="checkbox" checked={!!form.isFeatured} onChange={(e) => set("isFeatured", e.target.checked)} />
              Featured collection
            </label>
            <Field label="Display order">
              <input type="number" className={input} value={form.displayOrder ?? 0} onChange={(e) => set("displayOrder", Number(e.target.value))} />
            </Field>
            <Field label="Parent collection">
              <select className={input} value={form.parentId ?? ""} onChange={(e) => set("parentId", e.target.value || null)}>
                <option value="">None</option>
                {parents.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
            </Field>
          </section>

          {!isNew && existing && (
            <section className="grid gap-2 border border-black/10 bg-white p-5 text-xs text-black/60">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">Details</h3>
              <p>Products: <strong className="text-black">{existing.productCount}</strong></p>
              <p>Created: {new Date(existing.createdAt).toLocaleString("en-IN")}</p>
              <p>Updated: {new Date(existing.updatedAt).toLocaleString("en-IN")}</p>
              <Link to="/admin/products" search={{ collection: existing.slug } as never} className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-[#C6A15B] hover:underline">Manage products →</Link>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
